import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { RequestSentModal } from '@/study-helper-board/RequestSentModal';
import { StudyHelperRequestModal } from '@/study-helper-board/StudyHelperRequestModal';
import type { Column } from '@/tables/WbsTable';
import WbsTable from '@/tables/WbsTable';
import { AvatarImg, NameCell, PageContainer } from '~/styles/boardStyles';

type StudyHelperBoardItem = components['schemas']['StudyHelperBoardItem'];
type SwitchReason = components['schemas']['SwitchReason'];

export const Route = createFileRoute('/_app/study-helper-board/')({
  component: StudyHelperBoardScreen,
});

export default function StudyHelperBoardScreen() {
  const { t } = useTranslation();
  const { data: boardData } = api.getStudyHelperBoard.useQuery({
    query: { limit: 50 },
  });
  const { data: myStudyHelper } = api.getMyStudyHelper.useQuery({});
  const { mutateAsync: createAdoptionRequest } =
    api.createAdoptionRequest.useMutation();

  const helpers = useMemo(() => boardData?.data ?? [], [boardData]);
  const hasStudyHelper = myStudyHelper != null;

  const [selectedHelper, setSelectedHelper] =
    useState<StudyHelperBoardItem | null>(null);
  const [confirmedHelper, setConfirmedHelper] =
    useState<StudyHelperBoardItem | null>(null);

  const handleRequest = async (
    reason: SwitchReason | null,
    reasonOther: string | null,
  ): Promise<void> => {
    if (!selectedHelper) return;
    await createAdoptionRequest({
      body: {
        study_helper_id: selectedHelper.id,
        reason,
        reason_other: reasonOther,
      },
    });
    await api.getStudyHelperBoard.invalidateQueries();
  };

  const handleSubmitted = (): void => {
    setConfirmedHelper(selectedHelper);
    setSelectedHelper(null);
  };

  const columns: Column<StudyHelperBoardItem>[] = [
    {
      key: 'display_name',
      title: t('lessons.study-helper'),
      width: '290px',
      sortable: false,
      render: (_value, row) => (
        <NameCell>
          <AvatarImg src={row.avatar_url} alt={row.display_name} />
          <span>{row.display_name}</span>
        </NameCell>
      ),
    },
    {
      key: 'gender',
      title: t('wbs.gender'),
      sortable: true,
      width: '85px',
    },
    {
      key: 'country',
      title: t('wbs.location'),
      sortable: false,
      width: '250px',
      render: (_value, row) => {
        const location = row.region
          ? `${row.region}, ${row.country}`
          : row.country;
        return <span>{location}</span>;
      },
    },
  ];

  const renderActions = (helper: StudyHelperBoardItem) => {
    // Order matters. A student holding a pending request has `can_request`
    // false on EVERY row, so the label for the row they actually requested has
    // to win before the guard below hides it.
    if (helper.request_pending) {
      return <RequestSentLabel>{t('wbs.request-sent')}</RequestSentLabel>;
    }
    // `can_request` is legacy's page-level board suppression, expressed per
    // row: a pending request, an accepted one, or being claimed by a web target
    // or campaign while unadopted. The value is the same on every row.
    //
    // No explanatory copy: legacy replaced the table with a support address or
    // page content v3 doesn't have, so a blocked student sees a board with no
    // buttons. The wording is an open decision; don't add a sentence without
    // settling it.
    if (!helper.can_request) return null;
    return (
      <WBSButton onClick={() => setSelectedHelper(helper)}>
        {/* Two legacy actions: "Request to Switch" for a student with a
           study helper, "Request to be Adopted" for one without. */}
        {hasStudyHelper
          ? t('wbs.request-to-switch')
          : t('wbs.request-to-be-adopted')}
      </WBSButton>
    );
  };

  return (
    <PageContainer>
      <StudyHelperRequestModal
        isOpen={selectedHelper !== null}
        onClose={() => setSelectedHelper(null)}
        onSubmit={handleRequest}
        onSubmitted={handleSubmitted}
        variant={hasStudyHelper ? 'switch' : 'adopt'}
        requestedHelper={selectedHelper}
        currentHelper={myStudyHelper ?? undefined}
      />
      <RequestSentModal
        isOpen={confirmedHelper !== null}
        onClose={() => setConfirmedHelper(null)}
        helperName={confirmedHelper?.display_name ?? ''}
        variant={hasStudyHelper ? 'switch' : 'adopt'}
      />
      <div style={{ margin: '9px 0 29px 0' }}>
        <TitleWithQuestion
          title={t('lessons.study-helper-board')}
          placement="study_helper_board"
        />
      </div>
      <div style={{ marginTop: '24px' }}>
        <WbsTable
          data={helpers}
          columns={columns}
          showSearch
          showFilter
          showSettings
          renderActions={renderActions}
          cellStyle={{ padding: '14px 8px' }}
          hideRememberFilterCheckbox
          filterFields={[
            {
              key: 'location',
              label: t('wbs.location'),
              type: 'text',
            },
            {
              key: 'name',
              label: t('wbs.name'),
              type: 'text',
            },
          ]}
        />
      </div>
    </PageContainer>
  );
}

const RequestSentLabel = styled.span`
  color: var(--green);
  font-family: var(--raleway);
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
`;
